import { useEffect, useState } from 'react';
import Toggle from '../../common/Toggle';
import Button from '../../components/Button';
import EmptyState from '../../components/EmptyState';
import Header from '../../components/Header';
import Loader from '../../components/Loader';
import IconCustomerPortal from '../../components/icons/IconCustomerPortal';
import IconReport from '../../components/icons/IconReport';
import { StripeMode } from '../../types/pricingPage';
import { StripeSubscription } from '../../types/stripeSubscription';
import { useAxios } from '../../utils/hooks';
import UsageForm from './Form';
import c from 'classnames';

export default function UsageReporter() {
    const axios = useAxios();

    const [stripeMode, setStripeMode] = useState<StripeMode>(StripeMode.LIVE);
    const [subscriptions, setSubscriptions] = useState<StripeSubscription[]>();
    const [selectedSubscription, setSelectedSubscription] = useState<string>();

    useEffect(() => {
        if (!axios || !axios.current) {
            return;
        }
        setSubscriptions(undefined);
        axios.current.get(`/stripe/subscriptions?type=usage&mode=${stripeMode}`).then((res) => {
            setSubscriptions(res.data);
        });
    }, [axios, stripeMode]);

    const handleChangeStripeMode = () =>
        setStripeMode((old) => (old === StripeMode.LIVE ? StripeMode.TEST : StripeMode.LIVE));
    const handleSelectSubscription = (id: string) => (e: React.MouseEvent<HTMLButtonElement>) =>
        setSelectedSubscription(id);

    return (
        <div className="w-full">
            <Header className="flex">
                <IconReport className="inline w-6 mr-3" /> Report Subscription Usage
                <div className="ml-auto mr-0">
                    <Toggle
                        name="stripeMode"
                        checked={stripeMode === StripeMode.LIVE}
                        onChange={handleChangeStripeMode}
                        disabledColour="YELLOW"
                        text={{ checked: 'Live', unchecked: 'Test' }}
                    />
                </div>
            </Header>
            <div className="bg-white shadow overflow-hidden sm:rounded-lg max-w-4xl mx-auto mt-8">
                <div className="px-4 py-5 sm:px-6">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Subscriptions</h3>
                    <p className="mt-1 max-w-2xl text-sm text-gray-500"></p>
                    {!subscriptions && <Loader />}
                    {subscriptions && (
                        <>
                            {subscriptions.length === 0 && (
                                <EmptyState
                                    body={
                                        <div className="md:w-1/2 m-auto py-4">
                                            <h4 className="text-xl">No Usage-based Subscription Found</h4>
                                            <span className="mt-4">
                                                You are viewing {stripeMode === StripeMode.LIVE ? 'live' : 'test'} mode.
                                                Try switching to {stripeMode === StripeMode.LIVE ? 'test' : 'live'}{' '}
                                                mode.
                                            </span>
                                        </div>
                                    }
                                />
                            )}
                            {subscriptions.length > 0 && (
                                <ul className="border border-gray-200 rounded-md divide-y divide-gray-200">
                                    {subscriptions?.map((subscription) => (
                                        <li
                                            key={subscription.id}
                                            className={c('pl-3 pr-4 py-3 flex items-center justify-between text-sm', {
                                                'bg-gray-600 text-white': selectedSubscription === subscription.id,
                                            })}
                                        >
                                            <div className="w-0 flex-1 flex items-center">
                                                <IconCustomerPortal className="shrink-0 h-5 w-5 text-gray-400" />
                                                <a
                                                    href={`https://dashboard.stripe.com/${
                                                        stripeMode === StripeMode.TEST ? 'test/' : ''
                                                    }subscriptions/${subscription.id}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="ml-2 flex-1 w-0 truncate link"
                                                >
                                                    {subscription.id}
                                                </a>
                                            </div>
                                            <div className="ml-4 shrink-0">
                                                <Button
                                                    kind="outline"
                                                    onClick={handleSelectSubscription(subscription.id)}
                                                    disabled={selectedSubscription === subscription.id}
                                                >
                                                    Report Usage
                                                </Button>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </>
                    )}
                </div>
            </div>
            <UsageForm stripeMode={stripeMode} subscriptionId={selectedSubscription} />
        </div>
    );
}
